import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { cancelBooking, commissionSummary, recordReceipt, updateMilestone } from '../lib/api';
import { ageBucketLabel, copyText, dateOnly, friendlyError, istToday, rupees } from '../lib/format';
import type { AgeBucket, BookingRow, CommissionSummary, Milestone } from '../lib/types';
import { Badge, Button, Empty, ErrorText, PageHeader, TextField } from '../components/ui';

const BUCKETS: AgeBucket[] = ['not_invoiced', '0_30', '31_60', '61_90', 'over_90'];
const bucketTone: Record<AgeBucket, string> = { not_invoiced: 'text-muted', '0_30': 'text-ink', '31_60': 'text-warn', '61_90': 'text-bad', over_90: 'text-bad' };

export function Money() {
  const [data, setData] = useState<CommissionSummary | null>(null);
  const [includeTest, setIncludeTest] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try { setData(await commissionSummary(includeTest)); setError(''); } catch (e) { setError(friendlyError(e)); }
  }, [includeTest]);
  useEffect(() => { load(); }, [load]);
  const t = data?.totals;

  return (
    <div>
      <PageHeader title="Commission" subtitle="What builders owe you, worked out from each booking: commission, GST and TDS, by milestone, and how long it has been owed." />
      <ErrorText>{error}</ErrorText>
      <label className="mb-4 flex items-center gap-2 text-sm">
        <input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} /> Include test bookings from the simulator
      </label>

      {t && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Still owed" value={rupees(t.outstanding)} strong />
            <Stat label="Received" value={rupees(t.received)} />
            <Stat label="Expected in the bank" value={rupees(t.expected)} note={`Invoice ${rupees(t.invoice_total)} less TDS ${rupees(t.tds)}`} />
            <Stat label="Active bookings" value={String(t.bookings)} />
          </div>
          <section className="mb-6 rounded-2xl border border-line bg-surface p-4">
            <h2 className="mb-1 text-sm font-semibold uppercase tracking-wider text-muted">How long it has been owed</h2>
            <p className="mb-3 text-sm text-muted">Counted from the invoice date, or the due date until an invoice is raised.</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              {BUCKETS.map((b) => (
                <div key={b}>
                  <p className={`font-display text-xl ${Number(data!.ageing[b]) > 0 ? bucketTone[b] : 'text-muted'}`}>{rupees(data!.ageing[b])}</p>
                  <p className="text-xs text-muted">{ageBucketLabel[b]}</p>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      {data && data.bookings.length === 0 && (
        <Empty title="No bookings yet">Open a lead and use "Record booking" when a buyer books. The commission is tracked from there.</Empty>
      )}
      <ul className="flex flex-col gap-3">
        {data?.bookings.map((b) => <BookingCard key={b.id} b={b} onChanged={load} />)}
      </ul>
    </div>
  );
}

function Stat({ label, value, note, strong }: { label: string; value: string; note?: string; strong?: boolean }) {
  return (
    <div className="rounded-2xl border border-line bg-surface p-4">
      <p className={`font-display ${strong ? 'text-2xl text-accent' : 'text-2xl'}`}>{value}</p>
      <p className="text-sm text-muted">{label}</p>
      {note && <p className="mt-1 text-xs text-muted">{note}</p>}
    </div>
  );
}

type Mode = null | { invoice: string } | { pay: string } | 'cancel';

function BookingCard({ b, onChanged }: { b: BookingRow; onChanged: () => void }) {
  const { firm } = useAuth();
  const [mode, setMode] = useState<Mode>(null);
  const [copied, setCopied] = useState('');
  const cancelled = b.status === 'cancelled';
  const done = async () => { setMode(null); onChanged(); };

  function chaseText(m: Milestone) {
    return [
      `Dear ${b.builder ?? b.project_name} accounts team,`,
      '',
      `This is a reminder for our commission on the booking below.`,
      '',
      `Client: ${b.buyer_name ?? 'our client'}${b.unit_label ? `, unit ${b.unit_label}` : ''}, ${b.project_name}`,
      `Booked on: ${dateOnly(b.booked_on)}`,
      `Milestone: ${m.label}`,
      m.invoice_no ? `Our invoice: ${m.invoice_no} dated ${dateOnly(m.invoiced_on)}, ${rupees(m.invoice_total)} (incl. GST ${rupees(m.gst)})` : `Amount: ${rupees(m.invoice_total)} (incl. GST ${rupees(m.gst)})`,
      m.received > 0 ? `Received so far: ${rupees(m.received)}` : '',
      `Balance due after TDS: ${rupees(m.outstanding)}`,
      '',
      'Kindly release the payment or let us know if anything is needed from our side.',
      '',
      'Regards,',
      firm?.name ?? '',
    ].filter((x, i, a) => x !== '' || a[i - 1] !== '').join('\n');
  }

  return (
    <li className={`rounded-2xl border border-line bg-surface p-4 ${cancelled ? 'opacity-70' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium">
            {b.buyer_name ?? 'Buyer'} · {b.project_name}{b.unit_label ? ` · ${b.unit_label}` : ''}
            {b.is_test && <span className="ml-2"><Badge tone="warn">Test</Badge></span>}
            {cancelled && <span className="ml-2"><Badge tone="bad">Cancelled</Badge></span>}
          </p>
          <p className="text-sm text-muted">
            <Link to={`/leads?lead=${b.lead_id}`} className="text-accent hover:underline">L-{b.ref_no}</Link>
            {' '}· booked {dateOnly(b.booked_on)} · {rupees(b.agreement_value_inr)} at {Number(b.commission_pct)}% · GST {Number(b.gst_pct)}% · TDS {Number(b.tds_pct)}%
            {b.owner_name ? ` · ${b.owner_name}` : ''}
          </p>
          {b.cancelled_reason && <p className="text-sm text-bad">{b.cancelled_reason}</p>}
        </div>
        {!cancelled && (
          <div className="text-right">
            <p className="font-display text-xl">{rupees(b.outstanding)}</p>
            <p className="text-xs text-muted">still owed{b.oldest_days !== null && b.outstanding > 0 ? ` · oldest ${b.oldest_days} days` : ''}</p>
          </div>
        )}
      </div>

      {!cancelled && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[620px] text-sm">
            <thead className="text-left text-xs text-muted">
              <tr><th className="py-1 pr-2 font-medium">Milestone</th><th className="pr-2 text-right font-medium">Invoice (with GST)</th><th className="pr-2 text-right font-medium">TDS</th><th className="pr-2 text-right font-medium">Received</th><th className="pr-2 text-right font-medium">Owed</th><th className="font-medium">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-line">
              {b.milestones.map((m) => (
                <tr key={m.id} className="align-top">
                  <td className="py-2 pr-2">
                    {m.label} <span className="text-muted">({Number(m.share_pct)}%)</span>
                    <span className="block text-xs text-muted">{m.invoice_no ? `Invoice ${m.invoice_no} · ${dateOnly(m.invoiced_on)}` : m.due_on ? `Due ${dateOnly(m.due_on)}` : 'Not invoiced'}</span>
                  </td>
                  <td className="py-2 pr-2 text-right">{rupees(m.invoice_total)}</td>
                  <td className="py-2 pr-2 text-right">{rupees(Math.max(m.tds, m.tds_recorded))}</td>
                  <td className="py-2 pr-2 text-right">{rupees(m.received)}</td>
                  <td className="py-2 pr-2 text-right font-medium">{rupees(m.outstanding)}</td>
                  <td className="py-2">
                    {m.outstanding <= 0 ? <Badge tone="ok">Paid</Badge> : <Badge tone={m.bucket === '61_90' || m.bucket === 'over_90' ? 'bad' : m.bucket === '31_60' ? 'warn' : 'muted'}>{ageBucketLabel[m.bucket ?? 'not_invoiced']}</Badge>}
                    <div className="mt-1 flex flex-wrap gap-x-3 text-xs">
                      <button className="text-accent hover:underline" onClick={() => setMode({ invoice: m.id })}>{m.invoice_no ? 'Edit invoice' : 'Add invoice'}</button>
                      {m.outstanding > 0 && <button className="text-accent hover:underline" onClick={() => setMode({ pay: m.id })}>Record payment</button>}
                      {m.outstanding > 0 && (
                        <button className="text-accent hover:underline" onClick={async () => { await copyText(chaseText(m)); setCopied(m.id); setTimeout(() => setCopied(''), 2000); }}>
                          {copied === m.id ? 'Copied' : 'Copy reminder'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {b.milestones.some((m) => m.receipts.length) && (
            <details className="mt-2 text-sm">
              <summary className="cursor-pointer text-muted">Payments received</summary>
              <ul className="mt-1">
                {b.milestones.flatMap((m) => m.receipts.map((r, i) => (
                  <li key={`${m.id}-${i}`}>{dateOnly(r.received_on)} · {m.label} · {rupees(r.amount_inr)}{r.tds_inr ? ` · TDS ${rupees(r.tds_inr)}` : ''}{r.reference ? ` · ${r.reference}` : ''}{r.note ? ` · ${r.note}` : ''}</li>
                )))}
              </ul>
            </details>
          )}
        </div>
      )}

      {mode && typeof mode === 'object' && 'invoice' in mode && (
        <InvoiceForm m={b.milestones.find((x) => x.id === mode.invoice)!} bookedOn={b.booked_on} onDone={done} onCancel={() => setMode(null)} />
      )}
      {mode && typeof mode === 'object' && 'pay' in mode && (
        <PaymentForm m={b.milestones.find((x) => x.id === mode.pay)!} bookedOn={b.booked_on} onDone={done} onCancel={() => setMode(null)} />
      )}
      {mode === 'cancel' && <CancelForm b={b} onDone={done} onCancel={() => setMode(null)} />}
      {!cancelled && !mode && <div className="mt-3 text-right"><button className="text-xs text-muted hover:text-bad" onClick={() => setMode('cancel')}>Booking cancelled?</button></div>}
    </li>
  );
}

function useAction(onDone: () => Promise<void>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = (fn: () => Promise<unknown>) => async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setError('');
    try { await fn(); await onDone(); } catch (err) { setError(friendlyError(err)); setBusy(false); }
  };
  return { busy, error, run };
}
function Buttons({ busy, label, onCancel }: { busy: boolean; label: string; onCancel: () => void }) {
  return (
    <div className="flex justify-end gap-2">
      <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
      <Button type="submit" variant="primary" disabled={busy}>{busy ? 'Saving…' : label}</Button>
    </div>
  );
}

function InvoiceForm({ m, bookedOn, onDone, onCancel }: { m: Milestone; bookedOn: string; onDone: () => Promise<void>; onCancel: () => void }) {
  const [no, setNo] = useState(m.invoice_no ?? '');
  const [on, setOn] = useState(m.invoiced_on ?? istToday());
  const [due, setDue] = useState(m.due_on ?? '');
  const { busy, error, run } = useAction(onDone);
  return (
    <form onSubmit={run(() => updateMilestone(m.id, due || null, no, no ? on : null))} className="mt-3 flex flex-col gap-3 rounded-xl bg-surface2 p-3">
      <p className="text-sm font-medium">{m.label}: invoice for {rupees(m.invoice_total)} ({rupees(m.commission)} + GST {rupees(m.gst)})</p>
      <div className="grid gap-3 sm:grid-cols-3">
        <TextField label="Invoice number" value={no} onChange={(e) => setNo(e.target.value)} placeholder="NR/26-27/014" />
        <TextField label="Invoice date" type="date" min={bookedOn} max={istToday()} value={on} onChange={(e) => setOn(e.target.value)} />
        <TextField label="Payment due (optional)" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
      </div>
      <ErrorText>{error}</ErrorText>
      <Buttons busy={busy} label="Save invoice" onCancel={onCancel} />
    </form>
  );
}

function PaymentForm({ m, bookedOn, onDone, onCancel }: { m: Milestone; bookedOn: string; onDone: () => Promise<void>; onCancel: () => void }) {
  const [on, setOn] = useState(istToday());
  const [amount, setAmount] = useState(String(m.outstanding));
  const [tds, setTds] = useState(m.tds_recorded ? '0' : String(m.tds));
  const [ref, setRef] = useState('');
  const [note, setNote] = useState('');
  const { busy, error, run } = useAction(onDone);
  const amt = Number(amount.replace(/[^0-9-]/g, '')) || 0;
  return (
    <form onSubmit={run(() => recordReceipt({ milestoneId: m.id, receivedOn: on, amount: amt, tds: Number(tds) || 0, reference: ref, note }))}
      className="mt-3 flex flex-col gap-3 rounded-xl bg-surface2 p-3">
      <p className="text-sm font-medium">{m.label}: {rupees(m.outstanding)} still owed</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <TextField label="Received on" type="date" required min={bookedOn} max={istToday()} value={on} onChange={(e) => setOn(e.target.value)} />
        <TextField label="Amount in the bank (₹)" required inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} hint={amt ? rupees(amt) : undefined} />
        <TextField label="TDS deducted on this payment (₹)" inputMode="numeric" value={tds} onChange={(e) => setTds(e.target.value)} hint="Check it against Form 26AS later." />
        <TextField label="UTR or cheque number" value={ref} onChange={(e) => setRef(e.target.value)} />
      </div>
      <TextField label="Note" value={note} onChange={(e) => setNote(e.target.value)} hint="Payments cannot be edited. To correct one, record a negative amount with a note." />
      <ErrorText>{error}</ErrorText>
      <Buttons busy={busy} label="Record payment" onCancel={onCancel} />
    </form>
  );
}

function CancelForm({ b, onDone, onCancel }: { b: BookingRow; onDone: () => Promise<void>; onCancel: () => void }) {
  const [reason, setReason] = useState('');
  const { busy, error, run } = useAction(onDone);
  return (
    <form onSubmit={run(() => cancelBooking(b.id, reason))} className="mt-3 flex flex-col gap-3 rounded-xl bg-surface2 p-3">
      <TextField label="Why was the booking cancelled?" required value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Buyer cancelled within the cooling-off period" />
      <p className="text-xs text-muted">Its commission stops being counted as owed. Payments already recorded stay on record.</p>
      <ErrorText>{error}</ErrorText>
      <Buttons busy={busy} label="Mark cancelled" onCancel={onCancel} />
    </form>
  );
}
